import React, { useEffect, useState, useRef } from 'react';
import Chart from 'react-apexcharts';
import PropTypes from 'prop-types';
import { chartLabelStyles, chartFontFamily as fontFamily } from '../../constants';
import { getDataPoints, getBeginVsEndMonthColors, handleDataPointSelection, handleDataPointMouseEnter,
    getOptions } from '../../helpers/chart';
import PeriodComparison from '../common/PeriodComparison';
import { getChartTooltipContent } from '../../helpers/helpers';
import { dataPointMouseOverStyler } from '../../../agent-production/helpers/schemaTableHelpers';
import { getPdfClassname } from '../../helpers/pdf';
import Loader from '@lwt-helix/loader';
import { useSidebarExpanded } from '../../../common/hooks/useSidebarExpanded';

const SIDEBAR_EXPANDED_WIDTH = 300;
const SIDEBAR_COLLAPSED_WIDTH = 68;
const SCROLL_BREAKPOINT = 1200;
const MINIMUM_CHART_WIDTH = 1100;

const ResultsChart = ({ data, checkedStatusOptions, config, setSelectedPeriodComparison,
    selectedPeriodComparison, onBeginChange, onEndChange, agentData, selectedBeginIndex, selectedEndIndex, handleBarClick, isPDFChart, index, timePeriod, isOverview, overviewStatus }) => {

    const { isSideNavExpanded, isTransitioning } = useSidebarExpanded();

    const {threshold, lowHexColor, highHexColor, auxHexColor, xAxisLegends} = isOverview && config?.listingConfig?.chart || {};
    const intervalType = config?.groupConfig?.columnSettings?.label;
    const isStacked = config?.listingConfig?.chart?.isStacked;

    const options = getOptions({ chartLabelStyles, checkedStatusOptions, config, data, dataPointMouseOverStyler,
        fontFamily, getBeginVsEndMonthColors, getChartTooltipContent, handleBarClick, handleDataPointMouseEnter,
        handleDataPointSelection, highHexColor, intervalType, isOverview, isStacked, lowHexColor, overviewStatus, 
		selectedBeginIndex, selectedEndIndex, selectedPeriodComparison, threshold, xAxisLegends, isPDFChart, auxHexColor, timePeriod });

    const series = getDataPoints(checkedStatusOptions, data);
    const keyIncrement = 1;
    const [key, setKey] = useState(keyIncrement);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    const sidebarWidth = isSideNavExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH;
    const availableWidth = windowWidth - sidebarWidth + 200;
    const needsScroll = !isPDFChart && availableWidth < SCROLL_BREAKPOINT;

    const getAdjustedChartProps = (baseProps, needsScroll) => {
        const adjustedProps = { ...baseProps };
        adjustedProps.width = needsScroll ? `${MINIMUM_CHART_WIDTH}px` : '100%';
        return adjustedProps;
    };

    const chartProps = {
        key,
        options,
        series,
        type: 'bar',
        height: 390,
        width: '100%',
    };

    const adjustedChartProps = getAdjustedChartProps(chartProps, needsScroll);

    const shouldShowChart = data !== undefined;
    const isFirstRender = useRef(true);
    const isConfigReady = !!config?.listingConfig && !!config?.groupConfig;
    const wasSidebarTransitioning = useRef(false);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize, true);
        return () => window.removeEventListener('resize', handleResize, true);
    }, []);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setKey((prevKey) => prevKey + keyIncrement);
    }, [selectedBeginIndex, selectedEndIndex, selectedPeriodComparison]);

    useEffect(() => {
        if (wasSidebarTransitioning.current && !isTransitioning && !isPDFChart) {
            window.dispatchEvent(new Event('resize'));
        }
        wasSidebarTransitioning.current = isTransitioning;
    }, [isTransitioning, isPDFChart]);

    return (
        shouldShowChart && isConfigReady && (
            <div
                id="market-dynamics-chart-graph"
                className={`raised-border mb-3 bg-white market-dynamics-chart ${needsScroll ? 'needs-scroll' : 'no-scroll'}`}
            >
                {config?.groupConfig?.periodSelection && !isPDFChart && (
                    <div className="d-flex align-items-center">
                        <PeriodComparison
                            setSelectedPeriodComparison={setSelectedPeriodComparison}
                            selectedPeriodComparison={selectedPeriodComparison}
                            agentData={agentData}
                            intervalType={config?.groupConfig?.columnSettings?.label}
                            onBeginChange={onBeginChange}
                            onEndChange={onEndChange}
                        />
                    </div>
                )}
                {!isPDFChart && isTransitioning ? (
                    <div className="chart-loader">
                        <Loader />
                    </div>
                ) : (
                    <div className={`chart-wrapper ${needsScroll ? 'needs-scroll' : ''}`}>
                        <div className={isPDFChart ? getPdfClassname(isOverview, index) : ''}>
                            <Chart
                                {...adjustedChartProps}
                                width={needsScroll ? `${MINIMUM_CHART_WIDTH}px` : '100%'}
                                options={{
                                    ...adjustedChartProps.options,
                                    chart: {
                                        ...adjustedChartProps.options.chart,
                                        width: `${MINIMUM_CHART_WIDTH}px`
                                    }
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>
        )
    );
};

ResultsChart.propTypes = {
    data: PropTypes.array.isRequired,
    checkedStatusOptions: PropTypes.array,
    config: PropTypes.object,
    setSelectedPeriodComparison: PropTypes.func,
    selectedPeriodComparison: PropTypes.string,
    onBeginChange: PropTypes.func,
    onEndChange: PropTypes.func,
    agentData: PropTypes.array,
    selectedBeginIndex: PropTypes.number,
    selectedEndIndex: PropTypes.number,
    handleBarClick: PropTypes.func,
    isPDFChart: PropTypes.bool,
    index: PropTypes.number,
    timePeriod: PropTypes.string,
    isOverview: PropTypes.bool,
    overviewStatus: PropTypes.object,
};

export default ResultsChart;
